import { useCallback, useEffect, useReducer, useState } from "react";
// import { toast } from 'react-toastify';
import isEqual from "lodash/isEqual";
import noop from "lodash/noop";

// import { showToast } from '../helpers/externals/ShowToast';
// import { transformValidationError } from '../helpers/transform/transformValidationError';
import {
  initialRequestStatus,
  requestStatusReducer,
} from "./requestStatusReducer";
import { usePagination } from "./usePagination";
import { RequestStatus } from "../constants/RequestStatus";
import { IFieldConfig } from "../models/Table";
import { AxiosArrayResponse } from "../models/Response";
import { useQuery } from 'react-query';

export interface TableRequestParams {
  count: number;
  offset: number;
}

interface UseTableParams<Item, ExtraParams = Record<string, any>> {
  apiMethod: (
    params: TableRequestParams & ExtraParams
  ) => AxiosArrayResponse<Item>;
  extraParams?: ExtraParams;
  initialRowsPerPage?: number;
  initialPageCount?: number;
  fieldsConfig?: IFieldConfig[];
  transformResponse?: (data: Item[]) => any[];
  onParamsChange?: () => void;
}

export interface IUseTable<T> {
  requestStatus: RequestStatus;
  response: T[];
  page: number;
  rowsPerPage: number;
  rowsCount: number;
  fieldsConfig: IFieldConfig[];
  setPage: React.Dispatch<React.SetStateAction<number>>;
  setRowsPerPage: React.Dispatch<React.SetStateAction<number>>;
}

// @TODO add transform response as a param - not supported by ts3.5.1.
export const useTable = <Item, ExtraParams>(
  {
    apiMethod,
    fieldsConfig,
    initialRowsPerPage = 10,
    initialPageCount = 0,
    extraParams = {} as ExtraParams,
    transformResponse,
    onParamsChange = noop,
  }: UseTableParams<Item, ExtraParams>,
  deps: unknown[] = []
): IUseTable<Item> => {
  const [{ requestStatus }, dispatchRequest] = useReducer(
    requestStatusReducer,
    initialRequestStatus
  );
  // TODO fix in future

  const [rowsCount, setRowsCount] = useState(0);
  const [previousExtraParams, setPreviousExtraParams] = useState<ExtraParams>(
    extraParams
  );

  const { page, setPage, rowsPerPage, setRowsPerPage } = usePagination(
    initialRowsPerPage,
    initialPageCount
  );

  // const { data, isSuccess, isError, isLoading } = useQuery(
  //   ['/api/statsTable', { rowsPerPage, page }, extraParams, deps],
  //   (queryInfo) => {
  //     let internalOffset = page;
  //     if (!isEqual(previousExtraParams, extraParams)) {
  //       setPreviousExtraParams(extraParams);
  //       internalOffset = 0;
  //       setPage(0);
  //       onParamsChange();
  //     }
  //     dispatchRequest({ type: RequestStatus.PENDING });
  //     return apiMethod({
  //       count: rowsPerPage,
  //       offset: rowsPerPage * internalOffset,
  //       ...(extraParams && { ...extraParams }),
  //     });
  //   },
  //   {
  //     select: ({ data, count }) => {
  //       return {
  //         data: transformResponse ? transformResponse(data) : data,
  //         count
  //       }
  //     },
  //     onSuccess: (data) => {
  //       dispatchRequest({ type: RequestStatus.SUCCESS });
  //       setRowsCount(data.count);
  //     },
  //     onError: () => {
  //       dispatchRequest({ type: RequestStatus.FAILED });
  //     },
  //     // staleTime: 0
  //   },
  // );


  const [response, setResponse] = useState<Item[]>([]);

  const fetchData = useCallback(
    async (amount: number, offset: number) => {
      let internalOffset = offset;
      if (!isEqual(previousExtraParams, extraParams)) {
        setPreviousExtraParams(extraParams);
        internalOffset = 0;
        setPage(0);
        onParamsChange();
      }
      try {
        dispatchRequest({ type: RequestStatus.PENDING });
        const { data, count = 1 } = await apiMethod({
          count: amount,
          offset: amount * internalOffset,
          ...(extraParams && { ...extraParams }),
        });
        dispatchRequest({ type: RequestStatus.SUCCESS });
        transformResponse
          ? setResponse(transformResponse(data))
          : setResponse(data);
        setRowsCount(count);
      } catch ({ response }) {
        if (!response) {
          // add error boundary if backend failed
          return;
        }
        dispatchRequest({ type: RequestStatus.FAILED });
        // transformValidationError(response).map((el) =>
        //   showToast(el, toast.TYPE.ERROR)
        // );
      }
    },
    [extraParams]
  );

  useEffect(() => {
    fetchData(rowsPerPage, page);
  }, [rowsPerPage, page, ...deps]);

  return {
    requestStatus,
    // response: isSuccess ? data?.data : [],
    response,
    page,
    rowsPerPage,
    rowsCount,
    fieldsConfig,
    setPage,
    setRowsPerPage,
  };
};
